"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  id: "analysis",
  title: "Analyse",
  desc: "Wir identifizieren Schwachstellen in Ihrer Infrastruktur durch tiefgreifende Audits und Penetrationstests, um das Fundament für Ihre Sicherheit zu legen."
}, {
  id: "strategy",
  title: "Strategie",
  desc: "Entwicklung eines maßgeschneiderten Sicherheitskonzepts, das technologische Exzellenz mit Ihren spezifischen Geschäftsanforderungen vereint."
}, {
  id: "implementation",
  title: "Implementierung",
  desc: "Präzise Integration modernster Schutzmechanismen und Netzwerkarchitekturen durch unsere erfahrenen Systemspezialisten."
}, {
  id: "monitoring",
  title: "Monitoring",
  desc: "Kontinuierliche Überwachung und proaktive Anpassung Ihrer Systeme, um gegen die sich ständig entwickelnde Bedrohungslage gewappet zu sein."
}];
function ProcessStep({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-center gap-6 md:gap-16 py-8 md:py-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      {}
      <div className={`hidden md:flex w-1/2 ${isEven ? "justify-end text-right" : "order-last justify-start text-left"}`}>
        <div className="max-w-sm">
          <h3 className="text-3xl font-default font-bold text-foreground mb-4 tracking-tight">
            {step.title}
          </h3>
          <p className="text-muted-foreground text-lg leading-relaxed font-default">
            {step.desc}
          </p>
        </div>
      </div>

      {}
      <div className="relative z-10 flex items-center justify-center bg-background py-4 px-2">
        <span className="text-7xl md:text-9xl font-mono font-bold text-transparent [-webkit-text-stroke:1.5px_var(--color-primary)] md:[-webkit-text-stroke:2px_var(--color-primary)] opacity-40 select-none transition-opacity duration-700 hover:opacity-100">
          0{index + 1}
        </span>
      </div>

      {}
      <div className={`hidden md:block w-1/2 ${isEven ? "order-last" : ""}`} />

      {}
      <div className="md:hidden text-center px-6 max-w-md">
        <h3 className="text-2xl font-default font-bold text-foreground mb-3 tracking-tight">
          {step.title}
        </h3>
        <p className="text-muted-foreground leading-relaxed font-default">{step.desc}</p>
      </div>
    </div>;
}
export default function OurBakingProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return <section data-section-id="223559" id="der-securenetics-weg" className="py-24 md:py-32 bg-background text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {}
        <div ref={headerRef} className={`text-center mb-20 md:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-foreground mb-6 tracking-tighter">
            Der SecureNetics-Weg
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            Unsere methodische Herangehensweise garantiert kompromisslose Sicherheit für Ihre digitale Infrastruktur und Enterprise-Umgebungen.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-1/2 top-8 bottom-8 w-px bg-primary/30 -translate-x-1/2 shadow-[0_0_15px_rgba(0,255,255,0.3)]" />

          {}
          <div className="flex flex-col relative z-10">
            {steps.map((step, index) => <ProcessStep key={step.id} step={step} index={index} />)}
          </div>
        </div>

        {}
        <div className="mt-24 text-center relative z-20">
          <Button asChild size="lg" className="rounded-sm px-8 py-6 text-lg font-default bg-primary text-primary-foreground hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] transition-all">
            <Link to="/ContactPage">Sicherheit anfordern</Link>
          </Button>
        </div>
      </div>
    </section>;
}